/*
 * Authors: mariushirjanu
 * Created: 29/11/2022
 */

import { NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { UIRouterModule } from '@uirouter/angular';
import { STATE_PUNCHOUT, STATE_PUNCHOUT_CART, STATE_PUNCHOUT_CATALOG, STATE_PUNCHOUT_CATALOG_DETAIL, STATE_PUNCHOUT_CATALOG_OVERVIEW, STATE_PUNCHOUT_SHIPPING, STATE_PUNCHOUT_WEBSHOP } from '@app/shared/app-states';
import { Transition } from '@uirouter/angular';
import { PriceCartService } from '../cart/price-cart.service';
import { SharedModule } from '@app/shared/shared.module';
import { ShippingInfoComponent } from '../shipping/shipping-info.component';
import { OrderCatalogOverviewComponent } from '../catalog/overview/order-catalog-overview.component';
import { OrderCatalogDetailComponent } from '../catalog/detail/order-catalog-detail.component';
import { PunchoutComponent } from './punchout.component';
import { OrderEntryWebshopComponent } from '../entry/webshop/order-entry-webshop.component';
import { OrderEntryCatalogComponent } from '../entry/catalog/order-entry-catalog.component';
import { GaStateService, TranslationCategory } from '@app/shared/services/ga-state.service';
import { PunchoutCatalogService } from '../entry/catalog/punchout-catalog.service';
import { OrderCatalogDataService } from '../catalog/order-catalog-data.service';
import { PunchoutDataService } from './punchout-data.service';
import { PunchoutService } from './punchout.service';
import { OrderSharedModule } from '../order-shared.module';
import { CartComponent } from '../cart/cart.component';
import { OrderEntryWebshopService } from '../entry/webshop/order-entry-webshop.service';
import { GaStateDeclaration } from '@type_definitions/datatypes';
import { TRANSLATION_READY_RESOLVER } from '@app/app-routes.config';

export const catalogSearchParams:string = 'layout&pmin&pmax&sort&num&page&terms&cat&guide&filters&instock&fav&type';

const modules:TranslationCategory[] = [ TranslationCategory.ORDERENTRY, TranslationCategory.CATALOG ]; // load orderentry translation keys

const states:GaStateDeclaration[] = [
  {
    name: STATE_PUNCHOUT,
    url: '/punchout?punchoutMessageId&popup&part',
    abstract: true,
    component: PunchoutComponent,
    data: {
      translate: true,
      modules
    },
    resolve: [
      TRANSLATION_READY_RESOLVER,
      {
        token: 'punchoutMessageId',
        deps: [ Transition ],
        resolveFn: (trans:Transition) => {
          return trans.params().punchoutMessageId;
        }
      },
      {
        token: 'part',
        deps: [ Transition ],
        resolveFn: (trans:Transition) => {
          return trans.params().part;
        }
      },
      {
        token: 'popup',
        deps: [ Transition ],
        resolveFn: (trans:Transition) => {
          return trans.params().popup === 'true';
        }
      }
    ],
    params: {
      part: {
        dynamic: true
      }
    }
  },
  {
    name: STATE_PUNCHOUT_WEBSHOP,
    url: '',
    data: {
      viewTitle: 'Punchout - Select Webshop'
    },
    resolve: [TRANSLATION_READY_RESOLVER],
    component: OrderEntryWebshopComponent
  },
  {
    name: STATE_PUNCHOUT_CATALOG,
    url: '/catalog',
    abstract: true,
    component: OrderEntryCatalogComponent,
    resolve: [
      TRANSLATION_READY_RESOLVER,
      {
        token: 'partNumber',
        deps: [ PunchoutCatalogService, OrderCatalogDataService, GaStateService ],
        resolveFn: (punchoutCatalogService:PunchoutCatalogService, orderCatalogDataService:OrderCatalogDataService, gaStateService:GaStateService) => {
          if (punchoutCatalogService.punchoutPartNumber) {
            orderCatalogDataService.product(undefined, punchoutCatalogService.punchoutPartNumber, undefined, undefined, false)
              .then(() => {
                gaStateService.changeState(STATE_PUNCHOUT_CATALOG_DETAIL, { part: punchoutCatalogService.punchoutPartNumber });
              })
              .catch(() => {
                // clear part parameter if no detail found
                gaStateService.updateStateParameter('part', undefined);
                return Promise.resolve();
              });
          } else {
            return Promise.resolve();
          }
        }
      }
    ]
  },
  {
    name: STATE_PUNCHOUT_CATALOG_OVERVIEW,
    url: `?${catalogSearchParams}&compat`,
    data: {
      viewTitle: 'Punchout - Catalog'
    },
    component: OrderCatalogOverviewComponent,
    resolve: [TRANSLATION_READY_RESOLVER]
  },
  {
    name: STATE_PUNCHOUT_CATALOG_DETAIL,
    url: '/detail?product&bundle&compat',
    data: {
      viewTitle: 'Punchout - Product Details'
    },
    component: OrderCatalogDetailComponent,
    resolve: [TRANSLATION_READY_RESOLVER]
  },

  {
    name: STATE_PUNCHOUT_CART,
    url: '/cart',
    data: {
      viewTitle: 'Punchout - Shopping Cart'
    },
    component: CartComponent,
    resolve: [
      TRANSLATION_READY_RESOLVER,
      {
        token: 'punchout',
        resolveFn: () => true
      }
    ]
  },
  {
    name: STATE_PUNCHOUT_SHIPPING,
    url: '/shipping',
    data: {
      viewTitle: 'Punchout - Shipping'
    },
    component: ShippingInfoComponent,
    resolve: [
      {
        token: 'shopContext',
        deps: [ OrderEntryWebshopService ],
        resolveFn: (webshopService:OrderEntryWebshopService) => {
          return webshopService.shopContext;
        }
      }
    ]
  }
];

@NgModule({
  declarations: [
    PunchoutComponent
  ],
  imports: [
    UIRouterModule.forChild({ states }),
    CommonModule,
    SharedModule,
    OrderSharedModule
  ],
  providers: [
    PunchoutDataService,
    PunchoutService,
    PriceCartService
  ],
  exports: [
  ]
})
export class OrderPunchoutModule { }